import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * While true, clicks are swallowed so closing the drawer cannot activate
   * the PDP control sitting under the cursor (usually Add to cart).
   * @type {boolean}
   */
  #shieldClicks = false;

  /** @type {number | null} */
  #clickShieldTimeoutId = null;

  /**
   * Cart updates in this window must not auto-open the drawer. Covers an
   * add-to-cart request that already started from the click that closed it.
   * @type {number}
   */
  #suppressAutoOpenUntil = 0;

  /** @type {(() => void) | null} */
  #autoOpenReset = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartChange);
    window.addEventListener('click', this.#shieldClick, { capture: true });
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartChange);
    window.removeEventListener('click', this.#shieldClick, { capture: true });
    this.#clearClickShield();
    this.#clearAutoOpenReset();
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart change events - opens drawer if auto-open, announces count, and updates the empty state.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { itemCount?: number } }>} event
   */
  #handleCartChange = (event) => {
    // CartAddEvent `data.itemCount` represents added quantity, not total cart quantity.
    // Only use cart resource `item_count` when deciding empty/non-empty drawer state.
    const itemCount = event.detail?.resource?.item_count;

    if (this.hasAttribute('auto-open') && Date.now() > this.#suppressAutoOpenUntil) {
      this.showDialog();
    }

    this.#announceCartCount(itemCount);
    this.updateEmptyState(itemCount);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  /**
   * Closes the drawer and leaves the shopper on the page underneath.
   * Keep Shopping is painted over the PDP add-to-cart control. If this click
   * is allowed to continue after the dialog closes, the browser delivers it
   * to that button, which adds the product again and auto-opens the drawer.
   * @param {Event} [event]
   */
  close(event) {
    if (event instanceof Event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.#suppressAutoOpenUntil = Date.now() + 1500;
    this.#shieldClicks = true;
    this.#clearClickShield();
    this.#clickShieldTimeoutId = window.setTimeout(() => {
      this.#clickShieldTimeoutId = null;
      this.#shieldClicks = false;
      this.#armAutoOpenReset();
    }, 500);

    this.closeDialog().finally(() => {
      this.#focusPageContent();
    });
  }

  /**
   * Runs in the window capture phase, before the theme click delegate, so a
   * follow-up click never reaches Add to cart.
   * @param {Event} event
   */
  #shieldClick = (event) => {
    if (!this.#shieldClicks) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  #clearClickShield() {
    if (this.#clickShieldTimeoutId === null) return;

    clearTimeout(this.#clickShieldTimeoutId);
    this.#clickShieldTimeoutId = null;
  }

  /**
   * After the shield window, the next real pointer lets a later add-to-cart
   * auto-open the drawer again.
   */
  #armAutoOpenReset() {
    this.#clearAutoOpenReset();

    this.#autoOpenReset = () => {
      this.#suppressAutoOpenUntil = 0;
      this.#autoOpenReset = null;
    };

    window.addEventListener('pointerdown', this.#autoOpenReset, { capture: true, once: true });
  }

  #clearAutoOpenReset() {
    if (!this.#autoOpenReset) return;

    window.removeEventListener('pointerdown', this.#autoOpenReset, { capture: true });
    this.#autoOpenReset = null;
  }

  /**
   * Dialog close restores focus to whatever was focused before the drawer
   * opened. After Add to cart, that is the PDP button, and landing there
   * makes the next click add again. Move focus to the page instead.
   * Opening from the cart icon still returns focus to that control.
   */
  #focusPageContent() {
    const active = document.activeElement;
    const focusIsAddToCart =
      active instanceof HTMLElement &&
      (active.closest('add-to-cart-component') != null ||
        active.closest('.sticky-add-to-cart') != null ||
        active.classList.contains('add-to-cart-button'));

    if (!focusIsAddToCart) return;

    const main = document.getElementById('MainContent');
    if (!(main instanceof HTMLElement)) return;

    if (!main.hasAttribute('tabindex')) {
      main.setAttribute('tabindex', '-1');
    }

    main.focus({ preventScroll: true });
  }

  /**
   * The empty cart message is server-rendered, so only the dialog's class has to follow the cart.
   * @param {number | undefined} itemCount
   */
  updateEmptyState(itemCount) {
    this.refs.dialog?.classList.toggle('cart-drawer--empty', this.#isCartEmpty(itemCount));
  }

  /**
   * @param {number | undefined} itemCount
   * @returns {boolean}
   */
  #isCartEmpty(itemCount) {
    if (itemCount !== undefined) return itemCount === 0;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return false;

    return !dialog.querySelector('.cart-form');
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
